import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import ContentWrapper from "../../components/common/AssessmentContentWrapper";
import MainButton, {
  BackButton,
} from "../../components/common/MainButtonComponent";
import { routes } from "../../helper/routes";
import { assessmentStatusConstants } from "../../helper/constants";
import PaperCommentsDrawer from "./PaperCommentsDrawer";
import FinishReviewModal from "../assessment/FinishReviewModal";
import StudentPaperHeader from "./StudentPaperHeader";
import "./AssessShared.css";
import "./PaperReview.css";
import { AzureStorage } from "../../helper/azureStorage";
import config from "../../helper/config";
import DataHandler from "../../handlers/DataHandler";
import { sweetSuccessAlert, sweetErrorAlert, sweetConfirmAlert } from "../../components/common/SweetAlert";
import { sweetConfirmAlertWithChecklist } from "../../components/common/SweetAlert";
import {
  get_assessment_details_with_parts_sections,
  getAllQuestionsForAddedQuestionInSetupAndQuestionPool,
  MarkAsReviewed,
  addComment,
  finishPaperReview,
  addSubOperationComment,
  finishProofreading,

  set_review_for_paper,
  set_paper_to_publish,
  set_for_review_by_completed,
  finalize_paper,
  updateAssessmentStatus

} from "../../service/assessSections/assessSectionsService/assessSectionsService";
import PaperReviewQuestionEditPage from "./PaperReviewQuestionEditPage";
import { EditorState, convertFromRaw } from "draft-js";
import ViewEditor from "../../components/common/ViewEditor";
import {
  getQuestionId,
  getMainQuestions,
  getSubQuestionMap,
  groupQuestionsByPartAndSection,
  sortQuestionsForExam,
} from "./ExamQuestionGrouping";

const finishReviewOptions = [
  {
    key: assessmentStatusConstants.REFERBACK.name,
    value: "Refer Back",
  },
  {
    key: assessmentStatusConstants.ReviewCompleted.name,
    value: "Review Completed",
  },
];

const getRichTextContent = (value, fallback = "No content available.") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value !== "string") {
    return String(value);
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return fallback;
  }

  if (trimmedValue.startsWith("[") || trimmedValue.startsWith("{")) {
    try {
      const parsedValue = JSON.parse(trimmedValue);
      const entries = Array.isArray(parsedValue) ? parsedValue : [parsedValue];

      const text = entries
        .flatMap((entry) => entry?.blocks || [])
        .map((block) => block?.text || "")
        .filter(Boolean)
        .join("\n")
        .trim();

      return text || fallback;
    } catch {
      return trimmedValue;
    }
  }

  return trimmedValue;
};

const getDisplayValue = (value, fallback = "N/A") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return String(value);
};

const sortByDisplayOrder = (items = []) =>
  [...(Array.isArray(items) ? items : [])]
    .map((item, originalIndex) => ({ item, originalIndex }))
    .sort((a, b) => {
      const aOrder = Number(a.item?.display_order);
      const bOrder = Number(b.item?.display_order);
      const normalizedA = Number.isFinite(aOrder) ? aOrder : a.originalIndex;
      const normalizedB = Number.isFinite(bOrder) ? bOrder : b.originalIndex;

      return normalizedA - normalizedB || a.originalIndex - b.originalIndex;
    })
    .map(({ item }) => item);

const getOptionLabel = (options, value, fallback = "N/A") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const optionList = Array.isArray(options) ? options : [];
  const match = optionList.find((option) => String(option?.value) === String(value));
  return match?.label || String(value);
};

const getDraftTextContent = (value, fallback = "No content available.") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();

    if (!trimmedValue) return fallback;

    if (trimmedValue.startsWith("[") || trimmedValue.startsWith("{")) {
      try {
        const parsedValue = JSON.parse(trimmedValue);
        const entries = Array.isArray(parsedValue) ? parsedValue : [parsedValue];

        const text = entries
          .flatMap((entry) => entry?.blocks || [])
          .map((block) => block?.text || "")
          .filter(Boolean)
          .join("\n")
          .trim();

        return text || fallback;
      } catch {
        return trimmedValue;
      }
    }
    return trimmedValue;
  }

  if (Array.isArray(value)) {
    return value
      .flatMap((entry) => entry?.blocks || [])
      .map((block) => block?.text || "")
      .filter(Boolean)
      .join("\n")
      .trim() || fallback;
  }

  if (typeof value === "object") {
    const blocks = Array.isArray(value.blocks) ? value.blocks : [];
    const text = blocks
      .map((block) => block?.text || "")
      .filter(Boolean)
      .join("\n")
      .trim();

    return text || fallback;
  }

  return String(value);
};

const getCommentText = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    const normalizedValue = String(value).trim();
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return "";
};

const parseRawList = (value) => {
  try {
    if (!value) return [];

    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [];
  }
};

const toEditorState = (raw) => {
  try {
    if (!raw) return EditorState.createEmpty();

    return EditorState.createWithContent(convertFromRaw(raw));
  } catch {
    return EditorState.createEmpty();
  }
};

const QUESTION_TYPE = {
  MCQ: "1",
  DESCRIPTIVE: "2",
  SUBMISSION: "3",
  SUBQUESTION: "4",
};

const renderDraftContent = (content, className = "") => {
  const parts = parseRawList(content);

  if (!parts.length) {
    return <p className="text-muted mb-0">No content available.</p>;
  }

  return (
    <div className={className}>
      {parts.map((raw, index) => (
        <ViewEditor
          key={index}
          editorState={toEditorState(raw)}
        />
      ))}
    </div>
  );
};

const renderSingleDraft = (raw, className = "") => {
  if (!raw) return null;

  return (
    <div className={className}>
      <ViewEditor editorState={toEditorState(raw)} />
    </div>
  );
};

const getQuestionType = (question) => String(question?.qtype ?? question?.question_type ?? "");
const isSubQuestionType = (question) => getQuestionType(question) === QUESTION_TYPE.SUBQUESTION;
const isSubmissionQuestion = (question) => getQuestionType(question) === QUESTION_TYPE.SUBMISSION;

const parseAttachmentAnswer = (answer) => {
  try {
    const parsed = typeof answer === "string" ? JSON.parse(answer) : answer;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getMcqCorrectIndexes = (question) => {
  const answer = String(question?.answer ?? "");
  return answer
    .split(",")
    .map((value, index) => ({ value: Number(value.trim()), index }))
    .filter((item) => item.value > 0)
    .map((item) => item.index);
};

const getQuestionParts = (question) => {
  const contentParts = parseRawList(question?.content);
  return {
    questionRaw: contentParts[0],
    optionRaws: contentParts.slice(1),
  };
};

const getInitials = (name) => {
  if (!name || typeof name !== "string") return "";
  return name
    .trim()
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const isMcqQuestion = (question) => {
  const qtype = question?.qtype ?? question?.question_type ?? question?.type;
  return String(qtype) === "1" || String(qtype).toLowerCase() === "mcq";
};

const getQuestionOptionStates = (question) => {
  const contentList = parseRawList(question?.content ?? question?.question_content);
  const options = contentList.slice(1);
  const marks = String(question?.answer ?? question?.mcq_answer ?? "")
    .split(",")
    .map((value) => Number(value.trim()) || 0);

  const correctIndex = marks.findIndex((mark) => mark === 100);

  return {
    options,
    marks,
    correctIndex,
  };
};


const getSubQuestionIdentity = (subQuestion, index) => {
  return String(
    subQuestion?.question_id ??
    subQuestion?.questionId ??
    subQuestion?.assessment_question_id ??
    subQuestion?.id ??
    index
  );
};

const getQuestionIdentity = (question) => {
  return (
    question?.question_id ??
    question?.assessment_question_id

  );
};

const dedupeSubQuestions = (questions = []) => {
  const seen = new Set();

  return (Array.isArray(questions) ? questions : []).filter((subQuestion, index) => {
    const identity = getSubQuestionIdentity(subQuestion, index);

    if (seen.has(identity)) {
      return false;
    }

    seen.add(identity);
    return true;
  });
};

const resolveSubQuestions = (question, subQuestionMap = {}) => {
  const detailedSubQuestions = Array.isArray(question?.sub_questions_details)
    ? question.sub_questions_details.filter(
      (item) => item && typeof item === "object"
    )
    : [];
  const detailsById = new Map(
    detailedSubQuestions.map((item, index) => [
      getSubQuestionIdentity(item, index),
      item,
    ])
  );
  const linkedSubQuestions = [question?.sub_questions, question?.subquestions]
    .filter(Array.isArray)
    .map((items) => items.filter((item) => item && typeof item === "object"))
    .find((items) => items.length > 0);

  if (linkedSubQuestions) {
    return dedupeSubQuestions(
      linkedSubQuestions.map((item, index) =>
        normalizeQuestion({
          ...(detailsById.get(getSubQuestionIdentity(item, index)) || {}),
          ...item,
        })
      )
    );
  }

  if (detailedSubQuestions.length > 0) {
    return dedupeSubQuestions(detailedSubQuestions.map((item) => normalizeQuestion(item)));
  }

  const subQuestionIds = (Array.isArray(question?.sub_questions)
    ? question.sub_questions
    : String(question?.sub_questions || "").split(","))
    .map((item) => String(item).trim())
    .filter(Boolean);

  return dedupeSubQuestions(
    subQuestionIds
      .map((questionId) => normalizeQuestion(subQuestionMap[String(questionId)]))
      .filter(Boolean)
  );
};

const normalizeQuestion = (question, fallbackIndex = 0) => {

  const source = question?.question && typeof question.question === "object"
    ? question.question
    : question?.question_details && typeof question.question_details === "object"
      ? question.question_details
      : question || {};

  const subQuestions = Array.isArray(source.subquestions)
    ? source.subquestions
    : Array.isArray(source.sub_questions)
      ? source.sub_questions
      : Array.isArray(question?.subquestions)
        ? question.subquestions
        : Array.isArray(question?.sub_questions)
          ? question.sub_questions
          : [];

  return {
    ...question,
    ...source,
    question_number: question?.question_number ?? source.question_number ?? fallbackIndex + 1,
    title: source.title ?? question?.title ?? `Question ${fallbackIndex + 1}`,
    content: source.content ?? question?.content ?? "",
    answer: source.answer ?? question?.answer ?? "",
    guidance: source.guidance ?? source.guidence ?? question?.guidance ?? question?.guidence ?? "",
    mark_allocation:
      source.mark_allocation ??
      source.sub_mark_allocation ??
      question?.mark_allocation ??
      question?.sub_mark_allocation ??
      question?.markAllocation ??
      0,
    question_id: source.question_id ?? question?.question_id ?? question?.assessment_question_id,
    key: question?.assessment_question_id ?? source.question_id ?? question?.question_id ?? fallbackIndex,
    subquestions: subQuestions,
    part_id: question?.part_id ?? source.part_id,
    part_name: question?.part_name ?? source.part_name ?? "",
    part_instruction: question?.part_instruction ?? source.part_instruction ?? "",
    part_display_order: question?.part_display_order ?? source.part_display_order,
    section_id: question?.section_id ?? source.section_id,
    section_name:
      question?.section_name ??
      question?.section_title ??
      source.section_name ??
      source.section_title ??
      "",
    section_title:
      question?.section_title ??
      question?.section_name ??
      source.section_title ??
      source.section_name ??
      "",
    section_instruction: question?.section_instruction ?? source.section_instruction ?? "",
    section_display_order: question?.section_display_order ?? source.section_display_order,
    is_passage: question?.is_passage ?? source.is_passage ?? false,
    passage_id: question?.passage_id ?? source.passage_id ?? null,
    passage_content: question?.passage_content ?? source.passage_content ?? "",
  };
};

const groupQuestionsByPassage = (questionGroups = []) => {
  const groupedItems = [];

  questionGroups.forEach((group) => {
    const passageId = group.mainQuestion?.passage_id;

    if (!passageId) {
      groupedItems.push({
        passageId: null,
        passageContent: "",
        questions: [group],
      });
      return;
    }

    const existingGroup = groupedItems.find(
      (item) => String(item.passageId) === String(passageId)
    );

    if (existingGroup) {
      existingGroup.questions.push(group);
      return;
    }

    groupedItems.push({
      passageId,
      passageContent: group.mainQuestion?.passage_content || "",
      questions: [group],
    });
  });

  return groupedItems;
};


const getIsSubQuestionValue = (question) => {
  if (question && Object.prototype.hasOwnProperty.call(question, "is_subquestion")) {
    return question.is_subquestion;
  }

  if (question?.question && Object.prototype.hasOwnProperty.call(question.question, "is_subquestion")) {
    return question.question.is_subquestion;
  }

  return undefined;
};

const isMainQuestion = (question) => getIsSubQuestionValue(question) === null;


const calculateTotalAllocatedMarks = (questionGroups = []) => {
  return questionGroups.reduce((total, group) => {
    const mainQuestion = normalizeQuestion(group.mainQuestion);
    const subQuestions = dedupeSubQuestions(
      Array.isArray(group.subQuestions) && group.subQuestions.length > 0
        ? group.subQuestions
        : Array.isArray(mainQuestion.subquestions)
          ? mainQuestion.subquestions
          : []
    );

    if (subQuestions.length > 0) {
      const subQuestionTotalMarks = subQuestions.reduce(
        (sum, subQuestion) => sum + Number(subQuestion.mark_allocation || subQuestion.markAllocation || 0),
        0
      );
      return total + subQuestionTotalMarks;
    }

    return total + Number(mainQuestion.mark_allocation || mainQuestion.markAllocation || 0);
  }, 0);
};


const appendReviewDetail = (reviewState = {}, reviewDetail = {}) => {
  const existingDetails = Array.isArray(reviewState?.assessment_review_details)
    ? reviewState.assessment_review_details
    : [];
  const detailKey = String(
    reviewDetail?.review_detail_id ??
    reviewDetail?.asmnt_review_detail_id ??
    reviewDetail?.id ??
    `${reviewDetail?.question_id ?? "question"}-${reviewDetail?.created_at ?? Date.now()}`
  );

  const filteredDetails = existingDetails.filter((existingDetail) => {
    const existingKey = String(
      existingDetail?.review_detail_id ??
      existingDetail?.asmnt_review_detail_id ??
      existingDetail?.id ??
      `${existingDetail?.question_id ?? "question"}-${existingDetail?.created_at ?? ""}`
    );

    return existingKey !== detailKey;
  });

  return {
    ...reviewState,
    assessment_review_details: [...filteredDetails, reviewDetail],
  };
};

const getLatestOperationState = (operationState) => {
  if (Array.isArray(operationState)) {
    return operationState[0] || null;
  }

  return operationState || null;
};

const getLatestReviewState = (reviewState) => {
  if (Array.isArray(reviewState)) {
    return reviewState[0] || null;
  }

  return reviewState || null;
};

const normalizeProofreadingComments = (operationState) => {
  const comments = Array.isArray(operationState?.operation_details)
    ? operationState.operation_details
    : Array.isArray(operationState?.operation_details)
      ? operationState.operation_details
      : [];

  return comments.map((comment, index) => ({
    id:
      comment?.asmnt_operation_comment_id ??
      comment?.id ??
      `${operationState?.asmnt_operation_id ?? "operation"}-${index}`,
    comment: comment?.comment ?? comment?.text ?? comment?.content ?? "",
    created_at: comment?.created_at ?? comment?.updated_at ?? comment?.timestamp ?? "",
    updated_at: comment?.updated_at ?? null,

  }));
};

const pickUserIdCandidates = () => {
  const keys = [
    "manage_user_id",
    "table_log_id",
    "user_id",
    "email",
    "username",
    "employeeType",
    "userType",
    "user_role_selection",
  ];

  return keys.reduce((acc, key) => {
    acc[key] = {
      raw: sessionStorage.getItem(key),
      dataHandler: DataHandler.getFromSession(key),
    };
    return acc;
  }, {});
};


export default function PaperReviewViewPage({ item, EmployeeType, paperId, metaDataOptions: propsMetaDataOptions, onBack }) {
  console.log("PaperReviewViewPage item:", item);
  const history = useHistory();
  const location = useLocation();
  const params = useParams();


  const id = paperId || params.id;
  const metaDataOptions = propsMetaDataOptions || location.state?.metaDataOptions || {};

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentsDrawerMode, setCommentsDrawerMode] = useState("view");
  const [commentTargetQuestion, setCommentTargetQuestion] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [finishReviewError, setFinishReviewError] = useState("");
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [selectedQuestionData, setSelectedQuestionData] = useState(null);
  const [workflowActionLoading, setWorkflowActionLoading] = useState("");

  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [assessmentSetupDetails, setAssessmentSetupDetails] = useState(null);
  const [setupQuestionBank, setSetupQuestionBank] = useState([]);

  const paperHeaderAssessment = useMemo(() => {
    if (!assessmentDetails?.assessment) return null;

    const assessment = assessmentDetails.assessment;

    return {
      assessment_details: {
        ...assessment,

        grade:
          getOptionLabel(metaDataOptions.gradeOptions, assessment.grade_id, ""),

        subject:
          getOptionLabel(metaDataOptions.subjectOptions, assessment.subject_id, ""),

        medium:
          getOptionLabel(metaDataOptions.languageOptions, assessment.language_id, ""),

        author:
          assessment.created_by?.name ||
          assessment.created_by_name ||
          "",
      },

      start_time: null,
    };
  }, [assessmentDetails, metaDataOptions]);

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");


  const [reviewState, setReviewState] = useState(item?.review_state_latest || null);
  const assesmentReviewDetails = Array.isArray(reviewState?.assessment_review_details) ? reviewState.assessment_review_details : [];
  const normalizedAssessmentState = String(
    assessmentDetails?.assessment?.assessment_state || reviewState?.status || ""
  ).toLowerCase();
  const isReviewCompleted = normalizedAssessmentState === "reviewcompleted";
  const isReferBackState = normalizedAssessmentState === "referback";

  // keep this because the rest of the file still uses isReviewFinished
  const isReviewFinished = isReviewCompleted || isReferBackState;

  // only ReviewCompleted should hide all question action buttons
  // ReferBack must still allow Edit Question
  const hideQuestionActions = isReviewCompleted;


  const [openFinishReviewModal, setOpenFinishReviewModal] = useState(false);

  const [operationStateLatest, setOperationStateLatest] = useState(() => { return getLatestOperationState(item?.operation_state); });
  const [proofreadingComment, setProofreadingComment] = useState("");
  const [proofreadingComments, setProofreadingComments] = useState(() => { return normalizeProofreadingComments(getLatestOperationState(item?.operation_state)); });
  const [isProofredingComplteted, setIsProofreadingCompleted] = useState(() => {
    const opLatest = getLatestOperationState(item?.operation_state);
    return Boolean(opLatest?.operation_completed);
  });
  const proofreadingAuthorName = operationStateLatest?.teacher_name || "Reviewer";
  const workflowStatus = String(item?.workflowStatus || "").toLowerCase();
  const isWorkflowActionBusy = Boolean(workflowActionLoading);

  useEffect(() => {
    const latestOperationState = getLatestOperationState(item?.operation_state);

    setOperationStateLatest(latestOperationState);
    setProofreadingComments(normalizeProofreadingComments(latestOperationState));
    setIsProofreadingCompleted(Boolean(latestOperationState?.operation_completed));
  }, [item?.operation_state]);

  useEffect(() => {
    const latestReviewState = getLatestReviewState(item?.review_state_latest);

    setReviewState(latestReviewState);
  }, [item?.review_state_latest]);



  const loadAssessmentDetails = useCallback(async () => {

    setIsLoading(true);
    setLoadError("");

    try {
      const [response, setupResponse] = await Promise.all([
        get_assessment_details_with_parts_sections(id),
        getAllQuestionsForAddedQuestionInSetupAndQuestionPool(id).catch(() => null),
      ]);

      const setupData = setupResponse?.success ? setupResponse.data || {} : {};
      setAssessmentSetupDetails(setupData.assessmentDetails || null);
      setSetupQuestionBank(
        Array.isArray(setupData.questions_in_pool) ? setupData.questions_in_pool : []
      );

      if (response?.success) {
        const responseData = response?.data || {};
        setAssessmentDetails(responseData);

      } else {
        setAssessmentDetails(null);
        setLoadError(response?.message || "Unable to load assessment details.");
      }
    } catch {
      setAssessmentDetails(null);
      setAssessmentSetupDetails(null);
      setSetupQuestionBank([]);
      setLoadError("Unable to load assessment details.");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAssessmentDetails();
  }, [loadAssessmentDetails]);

  //-----------------------------------------------------------------------------------------------------------------------

  const assessment = assessmentDetails?.assessment || {};

  const assessmentState = String(
    assessment?.assessment_state || item?.status || ""
  ).toLowerCase();

  const isReadyToPublish = assessmentState === "readytopublish";
  const isReferBack = assessmentState === "referback";

  const returnApprovalDisabledMessage = isReferBack
    ? "Reviewer comments need to be resolved"
    : !isReadyToPublish
      ? "Paper is not ready for approval return"
      : "";

  const paperStructureParts = useMemo(() => {
    const reviewParts = Array.isArray(assessmentDetails?.parts)
      ? assessmentDetails.parts
      : [];

    if (reviewParts.length > 0) return reviewParts;

    return Array.isArray(assessmentSetupDetails?.parts)
      ? assessmentSetupDetails.parts
      : [];
  }, [assessmentDetails, assessmentSetupDetails]);

  const setupQuestionMap = useMemo(() => {
    const map = new Map();

    setupQuestionBank.forEach((question) => {
      const questionId = question?.question_id ?? question?.questionId ?? question?.id;
      if (questionId != null) map.set(String(questionId), question);

      const subQuestions = Array.isArray(question?.sub_questions_details)
        ? question.sub_questions_details
        : [];
      subQuestions.forEach((subQuestion) => {
        const subQuestionId =
          subQuestion?.question_id ?? subQuestion?.questionId ?? subQuestion?.id;
        if (subQuestionId != null) map.set(String(subQuestionId), subQuestion);
      });
    });

    return map;
  }, [setupQuestionBank]);

  const assessmentQuestions = useMemo(() => {
    const reviewQuestionById = new Map(
      (Array.isArray(assessmentDetails?.assessment_questions)
        ? assessmentDetails.assessment_questions
        : []
      ).map((question) => [String(question?.question_id), question])
    );
    const sectionContextById = new Map();
    paperStructureParts.forEach((part, partIndex) => {
      (Array.isArray(part.sections) ? part.sections : []).forEach((section, sectionIndex) => {
        const sectionId =
          section.part_section_id ?? section.assessment_section_id ?? section.section_id;
        if (sectionId == null) return;

        sectionContextById.set(String(sectionId), {
          part,
          partIndex,
          section,
          sectionIndex,
        });
      });
    });

    const normalizeReviewQuestion = (question, questionIndex, context = {}) => {
      const linkedReviewQuestion =
        reviewQuestionById.get(String(question?.question_id)) || question;
      const reviewQuestion =
        linkedReviewQuestion?.question && typeof linkedReviewQuestion.question === "object"
          ? linkedReviewQuestion.question
          : linkedReviewQuestion?.question_details &&
              typeof linkedReviewQuestion.question_details === "object"
            ? linkedReviewQuestion.question_details
            : {};
      const questionId =
        reviewQuestion.question_id ??
        question?.question_id ??
        question?.assessment_question_id;
      const setupQuestion = setupQuestionMap.get(String(questionId)) || {};
      const source = { ...setupQuestion, ...reviewQuestion };
      const { part, partIndex = 0, section, sectionIndex = 0 } = context;

      return normalizeQuestion(
        {
          ...question,
          ...linkedReviewQuestion,
          question: source,
          question_number:
            question?.question_number ?? linkedReviewQuestion?.question_number,
          passage_content:
            linkedReviewQuestion?.passage_content ??
            reviewQuestion.passage_content ??
            setupQuestion.passage_content ??
            "",
          is_passage:
            linkedReviewQuestion?.is_passage ??
            reviewQuestion.is_passage ??
            setupQuestion.is_passage ??
            false,
          passage_id:
            linkedReviewQuestion?.passage_id ??
            reviewQuestion.passage_id ??
            setupQuestion.passage_id ??
            null,
          part_id: part?.assessment_part_id ?? question?.part_id ?? "no-part",
          part_name:
            part?.part_title || part?.name || question?.part_name || `Part ${partIndex + 1}`,
          part_instruction: part?.part_instruction ?? question?.part_instruction ?? "",
          part_display_order: part?.display_order ?? question?.part_display_order ?? partIndex + 1,
          section_id:
            section?.part_section_id ??
            section?.assessment_section_id ??
            question?.section_id ??
            "no-section",
          section_name:
            section?.section_title ||
            section?.name ||
            question?.section_name ||
            `Section ${sectionIndex + 1}`,
          section_title:
            section?.section_title ||
            section?.name ||
            question?.section_title ||
            `Section ${sectionIndex + 1}`,
          section_instruction:
            section?.section_instruction ?? question?.section_instruction ?? "",
          section_display_order:
            section?.display_order ?? question?.section_display_order ?? sectionIndex + 1,
        },
        questionIndex
      );
    };

    const questionsFromParts = paperStructureParts.flatMap((part, partIndex) =>
      (Array.isArray(part.sections) ? part.sections : []).flatMap((section, sectionIndex) =>
        (Array.isArray(section.questions) ? section.questions : []).map((question, questionIndex) =>
          normalizeReviewQuestion(question, questionIndex, {
            part,
            partIndex,
            section,
            sectionIndex,
          })
        )
      )
    );

    if (questionsFromParts.length > 0) {
      return questionsFromParts;
    }

    return Array.isArray(assessmentDetails?.assessment_questions)
      ? assessmentDetails.assessment_questions.map((question, index) => {
        const context = sectionContextById.get(String(question?.section_id)) || {};
        return normalizeReviewQuestion(question, index, context);
      })
      : [];
  }, [assessmentDetails, paperStructureParts, setupQuestionMap]);

  const sortedQuestionList = useMemo(
    () => sortQuestionsForExam(assessmentQuestions),
    [assessmentQuestions]
  );

  const mainQuestionList = useMemo(
    () => getMainQuestions(sortedQuestionList),
    [sortedQuestionList]
  );

  const subQuestionMap = useMemo(
    () => getSubQuestionMap(sortedQuestionList),
    [sortedQuestionList]
  );

  const groupedQuestionBlocks = useMemo(() => {
    const sourceParts = paperStructureParts;

    if (!sourceParts.length) {
      return groupQuestionsByPartAndSection(mainQuestionList);
    }

    const notationValues = Array.isArray(assessmentSetupDetails?.notation_value)
      ? assessmentSetupDetails.notation_value
      : [];

    return sortByDisplayOrder(sourceParts).map((part, partIndex) => {
      const partKey = part.assessment_part_id ?? part.part_id ?? `part-${partIndex + 1}`;
      const notation = notationValues.find(
        (item) => Number(item?.part_id) === Number(part?.part_id)
      );
      const partName =
        part.part_title ||
        part.title ||
        part.name ||
        (notation?.name ? `Part ${notation.name}` : `Part ${partIndex + 1}`);

      return {
        partKey,
        partName,
        partInstruction: part.part_instruction ?? part.instructions ?? "",
        sections: sortByDisplayOrder(part.sections).map((section, sectionIndex) => {
          const sectionKey =
            section.part_section_id ??
            section.assessment_section_id ??
            section.section_id ??
            `section-${sectionIndex + 1}`;

          return {
            sectionKey,
            sectionName:
              section.section_title ||
              section.title ||
              section.name ||
              `Section ${sectionIndex + 1}`,
            sectionInstruction:
              section.section_instruction ?? section.instructions ?? "",
            questions: mainQuestionList.filter(
              (question) =>
                String(question.part_id) === String(partKey) &&
                String(question.section_id) === String(sectionKey)
            ),
          };
        }),
      };
    });
  }, [assessmentSetupDetails, mainQuestionList, paperStructureParts]);

  const parts = useMemo(
    () => paperStructureParts,
    [paperStructureParts]
  );
  const sectionsById = useMemo(() => {
    const entries = Array.isArray(assessmentDetails?.sections)
      ? assessmentDetails.sections
      : [];

    return new Map(entries.map((section) => [section.part_section_id, section]));
  }, [assessmentDetails]);
  const sectionsToRender = useMemo(() => {
    const sourceSections = Array.isArray(assessmentDetails?.sections)
      ? assessmentDetails.sections
      : [];

    if (parts.length > 0) {
      return parts.flatMap((part) =>
        (Array.isArray(part.sections) ? part.sections : []).map((section) => ({
          ...section,
          part_title: part.part_title,
          part_name: part.name,
        }))
      );
    }

    return sourceSections;
  }, [assessmentDetails, parts]);

  const sectionsByPart = useMemo(() => {
    const map = new Map();

    if (parts.length > 0) {
      parts.forEach((part) => {
        const partKey = String(part.assessment_part_id ?? part.part_id ?? part.name ?? part.part_title);
        map.set(partKey, part);
      });
      return map;
    }

    sectionsToRender.forEach((section) => {
      const partKey = String(section.assessment_part_id ?? section.part_id ?? section.part_title ?? section.part_name ?? "part");
      if (!map.has(partKey)) {
        map.set(partKey, {
          part_id: section.part_id ?? section.assessment_part_id ?? null,
          assessment_part_id: section.assessment_part_id ?? null,
          part_title: section.part_title ?? section.part_name ?? "Part",
          name: section.part_name ?? section.part_title ?? "",
          sections: [],
        });
      }
      map.get(partKey).sections.push(section);
    });

    return map;
  }, [parts, sectionsToRender]);
  //------------------------------------------------------------------------------------------------------------------------

  const isQuestionReviewed = useCallback(
    (questionId) => {
      const questionKey = String(questionId);

      return assesmentReviewDetails.some((reviewDetail) => {
        const reviewQuestionId =
          reviewDetail?.question_id ??
          reviewDetail?.assessment_question_id ??
          reviewDetail?.id;

        if (String(reviewQuestionId) !== questionKey) {
          return false;
        }

        return (
          reviewDetail?.reviewed === true ||
          reviewDetail?.reviewed === 1 ||
          reviewDetail?.reviewed === "1" ||
          reviewDetail?.mark_as_reviewed === true ||
          reviewDetail?.mark_as_reviewed === 1 ||
          reviewDetail?.mark_as_reviewed === "1"
        );
      });
    },
    [assesmentReviewDetails]
  );

  const reviewedQuestionIds = useMemo(() => {
    const collectedIds = new Set();

    mainQuestionList.forEach((question) => {
      const questionId = getQuestionId(question);

      if (questionId !== null && questionId !== undefined && questionId !== "") {
        collectedIds.add(String(questionId));
      }
    });

    return collectedIds;
  }, [mainQuestionList]);

  const allQuestionsReviewed = useMemo(() => {
    if (reviewedQuestionIds.size === 0) {
      return false;
    }

    return Array.from(reviewedQuestionIds).every((questionId) => isQuestionReviewed(questionId));
  }, [isQuestionReviewed, reviewedQuestionIds]);

  const finalizeDisabledMessage = isReferBack
    ? "Reviewer comments need to be resolved"
    : !isReadyToPublish
      ? "Review needs to be finished before finalizing"
      : "";

  useEffect(() => {
    console.log("[Finish Review Debug]", {
      reviewedQuestionIds: Array.from(reviewedQuestionIds),
      assesmentReviewDetails,
      reviewedStatus: Array.from(reviewedQuestionIds).map((questionId) => ({
        questionId,
        reviewed: isQuestionReviewed(questionId),
      })),
      allQuestionsReviewed,
    });
  }, [reviewedQuestionIds, assesmentReviewDetails, isQuestionReviewed, allQuestionsReviewed]);

  
  const loggedUserIdCandidates = [
    DataHandler.getFromSession("manage_user_id"),
    DataHandler.getFromSession("table_log_id"),
    DataHandler.getFromSession("user_id"),
    sessionStorage.getItem("manage_user_id"),
    sessionStorage.getItem("table_log_id"),
    sessionStorage.getItem("user_id"),
  ]
    .filter(Boolean)
    .map((value) => String(value));

  const loggedUserId = loggedUserIdCandidates[0] || "";

  const currentReview =
    reviewState ||
    item?.review_state_latest ||
    item?.review_state?.[0] ||
    {};
  const latestReview = currentReview;
  const latestOperation = operationStateLatest || item?.operation_state_latest || {};
  const currentReviewId =
    currentReview?.asmnt_review_id ||
    currentReview?.review_id ||
    "";

  const reviewAssignedUserId = String(
    currentReview?.teacher_id ??
    currentReview?.reviewer_id ??
    currentReview?.employee_id ??
    item?.assignedToId ??
    ""
  );

  const operationAssignedUserId = String(
    latestOperation?.employee_id ??
    latestOperation?.teacher_id ??
    latestOperation?.reviewer_id ??
    item?.assignedToId ??
    ""
  );

  const isTeachAssignedPaper =
  EmployeeType === "TEACH" &&
  assessmentState !== "proofreading" &&
  assessmentState !== "dataentryverification";

  const isDeopAssignedOperation =
    EmployeeType === "DEOP" &&
    (assessmentState === "proofreading" || assessmentState === "dataentryverification");

  const isAssignedReviewer =
    isTeachAssignedPaper ||
    (
      !!reviewAssignedUserId &&
      loggedUserIdCandidates.includes(String(reviewAssignedUserId))
    );

  const isAssignedOperator =
    isDeopAssignedOperation ||
    (
      !!operationAssignedUserId &&
      loggedUserIdCandidates.includes(String(operationAssignedUserId))
    );

  const canMarkReviewed =
    EmployeeType === "TEACH" &&
    isAssignedReviewer &&
    ["review", "inreview"].includes(assessmentState);

  const canAddQuestionComment =
    EmployeeType === "TEACH" &&
    isAssignedReviewer &&
    ["review", "inreview"].includes(assessmentState);

  const canEditQuestion =
    ["MASAD", "DEOP", "TEACH"].includes(EmployeeType) &&
    ["review", "inreview", "referback"].includes(assessmentState);

  const canEditProofreadingQuestion =
    assessmentState === "proofreading" &&
    !isProofredingComplteted &&
    isAssignedOperator;

  const canFinishReview =
    EmployeeType === "TEACH" &&
    isAssignedReviewer &&
    ["review", "inreview"].includes(assessmentState) &&
    !isReviewFinished;

  const canFinishOperation =
    isAssignedOperator &&
    ["proofreading", "dataentryverification"].includes(assessmentState) &&
    !isProofredingComplteted;

  const canAddProofreadingComment =
    isAssignedOperator &&
    !isProofredingComplteted &&
    !isReferBack;

  const canSetForReview =
    ["DEOP", "MASAD"].includes(EmployeeType) &&
    isReferBack;

  const canSetBackToReview =
    EmployeeType === "DEOP" &&
    assessmentState === "reviewcompleted";

  const canSetReadyToPublish =
    EmployeeType === "DEOP" &&
    assessmentState === "reviewcompleted";

  const canReturnApproval =
    EmployeeType === "MASAD" &&
    isReadyToPublish;

  const canFinalizePaper =
    EmployeeType === "MASAD" &&
    isReadyToPublish;

  useEffect(() => {
    const userCandidates = pickUserIdCandidates();

    console.log("[Paper Review User ID Candidates]", userCandidates);

    console.log("[Paper Review Assigned Source]", {
      item,
      reviewState,
      operationStateLatest,
      latestReview,
      latestOperation,
      itemAssignedToId: item?.assignedToId,
      itemAssignedTo: item?.assignedTo,
      reviewAssignedUserId,
      operationAssignedUserId,
    });

    console.log("[Paper Review Permission Debug]", {
      EmployeeType,
      assessmentState,
      workflowStatus,

      rawSession: {
        manage_user_id: sessionStorage.getItem("manage_user_id"),
        table_log_id: sessionStorage.getItem("table_log_id"),
        user_id: sessionStorage.getItem("user_id"),
        email: sessionStorage.getItem("email"),
        username: sessionStorage.getItem("username"),
      },

      dataHandler: {
        manage_user_id: DataHandler.getFromSession("manage_user_id"),
        table_log_id: DataHandler.getFromSession("table_log_id"),
        user_id: DataHandler.getFromSession("user_id"),
        email: DataHandler.getFromSession("email"),
        username: DataHandler.getFromSession("username"),
      },

      latestReview,
      latestOperation,
      reviewAssignedUserId,
      operationAssignedUserId,

      loggedUserId,
      isAssignedReviewer,
      isAssignedOperator,

      permissions: {
        canMarkReviewed,
        canAddQuestionComment,
        canEditQuestion,
        canEditProofreadingQuestion,
        canFinishReview,
        canFinishOperation,
        canAddProofreadingComment,
        canSetForReview,
        canSetBackToReview,
        canSetReadyToPublish,
        canReturnApproval,
        canFinalizePaper,
      },
    });
  }, [
    EmployeeType,
    assessmentState,
    workflowStatus,
    latestReview,
    latestOperation,
    reviewAssignedUserId,
    operationAssignedUserId,
    loggedUserId,
    isAssignedReviewer,
    isAssignedOperator,
  ]);
  const selectedQuestion = useMemo(() => {
    if (selectedQuestionData) return selectedQuestionData;
    if (!selectedQuestionId) return null;

    const matchedQuestion = assessmentQuestions.find((question) => {
      const questionId = getQuestionId(normalizeQuestion(question));
      return String(questionId) === String(selectedQuestionId);
    });

    return matchedQuestion ? normalizeQuestion(matchedQuestion) : null;
  }, [assessmentQuestions, selectedQuestionId]);



  const aggregatedComments = useMemo(() => {
    const allComments = [];
    const review_state_latest = reviewState || {};

    assesmentReviewDetails.forEach((rd, idx) => {
      const questionId = String(rd?.question_id ?? "");
      const commentText = getCommentText(rd?.comment);

      if (questionId && rd && commentText) {
        const uniqueId =
          rd?.review_detail_id ?? rd?.asmnt_review_detail_id ?? rd?.id ?? `${questionId}-${idx}-${rd?.created_at ?? ""}`;

        allComments.push({
          id: String(uniqueId),
          question_id: questionId,
          reviewer_name: rd?.reviewer_name || review_state_latest.teacher_name || sessionStorage.getItem("manage_user_name") || "Reviewer",
          reviewer_created_at: rd.created_at || rd.updated_at || "",
          comment: commentText,
          mark_as_reviewed: rd.reviewed,
        });
      }
    });

    // Sort by question number (if present) then by time (newest first)
    return allComments.sort((a, b) => {
      const qNumA = a.question_number || 0;
      const qNumB = b.question_number || 0;
      if (qNumA !== qNumB) {
        return qNumA - qNumB;
      }
      const timeA = new Date(a.reviewer_created_at || 0).getTime();
      const timeB = new Date(b.reviewer_created_at || 0).getTime();
      return timeB - timeA;
    });
  }, [assesmentReviewDetails, reviewState]);

  
  const totalQuestions = useMemo(() => {
    return mainQuestionList.length;
  }, [mainQuestionList]);


  const totalAllocatedMarks = useMemo(() => {
    return groupedQuestionBlocks.reduce((grandTotal, partGroup) => {
      const partTotal = partGroup.sections.reduce((sectionTotal, sectionGroup) => {
        const questionGroups = sectionGroup.questions.map((question, index) => {
          const subQuestions = resolveSubQuestions(question, subQuestionMap);

          return {
            key: String(getQuestionId(question) || index),
            questionNumber: question.question_number || index + 1,
            mainQuestion: question,
            subQuestions,
          };
        });

        return sectionTotal + calculateTotalAllocatedMarks(questionGroups);
      }, 0);

      return grandTotal + partTotal;
    }, 0);
  }, [groupedQuestionBlocks, subQuestionMap]);

  console.log("created_by", assessment.created_by);

  const createdByLabel =
    metaDataOptions?.createdByOptions?.find(
      (option) =>
        String(option?.value) === String(assessment?.created_by)
    )?.label ||
    assessment?.created_by ||
    "N/A";

  const assessmentDetailItems = [
    { label: "Assessment ID", value: 'ASMNT' + getDisplayValue(assessment.assessment_id) },
    { label: "Title", value: getDisplayValue(assessment.title) },
    { label: "Grade", value: getOptionLabel(metaDataOptions.gradeOptions, assessment.grade_id) },
    { label: "Subject", value: getOptionLabel(metaDataOptions.subjectOptions, assessment.subject_id) },
    { label: "Language", value: getOptionLabel(metaDataOptions.languageOptions, assessment.language_id) },
    { label: "Year", value: getOptionLabel(metaDataOptions.yearOptions, assessment.year_id) },
    { label: "Term", value: getOptionLabel(metaDataOptions.termOptions, assessment.term_id) },
    { label: "Chapter", value: getOptionLabel(metaDataOptions.chapterOptions, assessment.chapter_id) },
    { label: "Difficulty", value: getOptionLabel(metaDataOptions.difficultyOptions, assessment.difficulty_id) },
    { label: "Exam Category", value: getOptionLabel(metaDataOptions.examCategoryOptions, assessment.exam_category_id) },
    // { label: "Created By", value: getOptionLabel(metaDataOptions.createdByOptions, assessment.created_by || assessment.created_by_name ) },
    {
      label: "Created By",
      value: getDisplayValue(
        typeof assessment.created_by === "object"
          ? assessment.created_by.name
          : assessment.created_by
      )
    },
    { label: "Source", value: getOptionLabel(metaDataOptions.sourceOptions, assessment.source_id) },
    // { label: "Parts", value: getDisplayValue(assessment.no_of_parts, "0") },
    {
      label: "Parts",
      value: getDisplayValue(parts.length, "0")
    },
    { label: "Questions", value: getDisplayValue(totalQuestions, "0") },
  ];


  const getCommentsForQuestion = useCallback(
    (questionId) =>
      Array.isArray(aggregatedComments)
        ? aggregatedComments
          .filter((c) => String(c.question_id) === String(questionId))
          .slice()
          .sort((a, b) => new Date(b.reviewer_created_at || 0).getTime() - new Date(a.reviewer_created_at || 0).getTime())
        : [],
    [aggregatedComments]
  );

  // Ensure comment lists show newest-first at top when comments update or drawer opens
  useEffect(() => {
    try {
      const lists = document.querySelectorAll(
        ".paper-review-info__box.paper-review-comments-list"
      );
      lists.forEach((el) => {
        // newest-first is at top, ensure scrollTop is 0
        el.scrollTop = 0;
      });
    } catch (e) {
      // ignore
    }
  }, [aggregatedComments, commentsOpen]);

  const openCommentDrawer = useCallback((mode = "view", question = null, currentQIndex = null) => {
    setCommentsDrawerMode(mode);
    setCommentTargetQuestion(question ? { ...question, currentQIndex } : null);
    setCommentDraft("");
    setCommentError("");
    setCommentsOpen(true);
  }, []);

  const closeCommentDrawer = useCallback(() => {
    setCommentsOpen(false);
    setCommentsDrawerMode("view");
    setCommentTargetQuestion(null);
    setCommentDraft("");
    setCommentError("");
  }, []);


  const handleSubmitComment = useCallback(async () => {
    const questionId =
      commentTargetQuestion?.question_id ??
      commentTargetQuestion?.assessment_question_id ??
      commentTargetQuestion?.id;

    const trimmedComment = commentDraft.trim();

    if (!questionId) {
      setCommentError("Select a question before adding a comment.");
      return;
    }

    if (!trimmedComment) {
      setCommentError("Comment cannot be empty.");
      return;
    }

    const assessmentId = assessment?.assessment_id || id;
    const reviewerId = DataHandler.getFromSession("manage_user_id") || reviewState?.reviewer_id || "";
    const reviewerTableId = DataHandler.getFromSession("table_log_id") || reviewState?.reviewer_table_id || "";

    if (!reviewerId || !reviewerTableId) {
      setCommentError("Reviewer session is missing. Please sign in again.");
      return;
    }

    // Backend currently expects assessment id in asmnt_review_id for review lookup.
    const asmntReviewId = reviewState?.asmnt_id ?? assessmentId;
    const reviewId = reviewState?.asmnt_review_id ?? reviewState?.review_id ?? reviewState?.asmnt_id ?? "";
    const currentQIndex = Number(
      commentTargetQuestion?.currentQIndex ??
      commentTargetQuestion?.question_number ??
      commentTargetQuestion?.question_index ??
      0
    ) || 0;

    const payload = {
      review: [
        {
          currentQIndex,
          question_content_index: currentQIndex,
          comment: trimmedComment,
          asmnt_review_id: asmntReviewId,
          assesment_id: assessmentId,
          question_id: questionId,
          review_id: reviewId,
          review_status: "InReview",
          reviewer_id: reviewerId,
          reviewer_table_id: reviewerTableId,
          mark_as_reviewed: 0,
        },
      ],
    };

    const nextReviewState = appendReviewDetail(reviewState, payload.review[0]);
    setReviewState(nextReviewState);

    try {
      setCommentSubmitting(true);
      setCommentError("");

      const response = await addComment(assessmentId, payload);

      if (response?.success === false) {
        setCommentError(response?.message || "Unable to add comment.");
        return;
      }


      closeCommentDrawer();
    } catch (error) {
      console.error("Error adding comment:", error);
      setCommentError(error?.message || "Unable to add comment.");
    } finally {
      setCommentSubmitting(false);
    }
  }, [
    assessment?.assessment_id,
    reviewState,
    commentDraft,
    commentTargetQuestion,
    closeCommentDrawer,
    id,
    loadAssessmentDetails,
  ]);


  const handleFinishReview = useCallback(
    async (selectedStatus) => {
      const assessmentId = assessment?.assessment_id || id;
      const reviewerId = DataHandler.getFromSession("manage_user_id") || reviewState?.reviewer_id || "";
      const asmntReviewId =
        reviewState?.asmnt_review_id ||
        reviewState?.review_id ||
        reviewState?.asmnt_id ||
        "";

      if (!assessmentId || !reviewerId || !asmntReviewId) {
        setLoadError("Reviewer session is missing. Please sign in again.");
        return;
      }


      setFinishReviewError("");

      const response = await finishPaperReview(
        assessmentId,
        reviewerId,
        asmntReviewId,
        selectedStatus
      );

      if (response?.success === false) {
        setLoadError(response?.message || "Unable to finish the review.");
        return;
      }

      await loadAssessmentDetails();
      setOpenFinishReviewModal(false);
    },
    [reviewState, allQuestionsReviewed, assessment?.assessment_id, id, loadAssessmentDetails]
  );

  const handelChangeMrkedAsReviewed = async (questionId) => {
    try {

      await sweetConfirmAlertWithChecklist();

      const assessmentId = assessment?.assessment_id || id;
      const questionKey = String(questionId);
      const isCurrentlyReviewed = isQuestionReviewed(questionId);

      const payload = {
        review: [
          {
            question_content_index: 0,
            question_id: questionId,
            comment: "",
            assesment_id: assessmentId,
            mark_as_reviewed: isCurrentlyReviewed ? 0 : 1,
            review_status: "InReview",
            reviewer_id: DataHandler.getFromSession("manage_user_id") || "",
            reviewer_table_id: DataHandler.getFromSession("table_log_id") || ""
          }
        ]
      };

      const nextReviewState = appendReviewDetail(reviewState, payload.review[0]);
      setReviewState(nextReviewState);
      const response = await MarkAsReviewed(assessmentId, payload);

      if (!response?.success) {
        alert(response?.message || "Unable to update review status.");
        return;
      }


    } catch (error) {
      console.error("Error marking question as reviewed:", error);
      alert("Error: " + error.message);
    }
  }

  const formatTime = useCallback((t) => {
    if (!t) return "";
    try {
      const d = new Date(t);
      if (isNaN(d.getTime())) return String(t);
      return d.toLocaleString();
    } catch {
      return String(t);
    }
  }, []);


  const editQuestion = (question, subQuestions = []) => {
    const questionId =
      question?.question_id ?? question?.assessment_question_id ?? question?.id;
    setSelectedQuestionId(questionId);
    setSelectedQuestionData({
      ...question,
      subquestions: Array.isArray(subQuestions) ? subQuestions : [],
      sub_questions: Array.isArray(subQuestions) ? subQuestions : [],
      sub_questions_details: Array.isArray(subQuestions) ? subQuestions : [],
    });
  };

  const handleQuestionUpdated = async (result) => {
    const updatedQuestion = result?.updatedQuestion;

    if (updatedQuestion) {
      setSelectedQuestionData(updatedQuestion);
    }

    setSelectedQuestionId(null);
    setSelectedQuestionData(null);

    await loadAssessmentDetails();
  };

  const handleProofreadingCommentSubmit = useCallback(async () => {
    const trimmedComment = proofreadingComment.trim();

    if (!trimmedComment) {
      alert("Please enter a comment .");
      return;
    }

    const payload = {
      review:
      {
        assessment_id: assessment?.assessment_id || id,
        comment: trimmedComment,
        employee_id: sessionStorage.getItem("manage_user_id") || "",
        reviewer_id: sessionStorage.getItem("manage_user_id") || "",
        reviewer_table_id: sessionStorage.getItem("table_log_id") || "",

      },

    };
    try {
      const response = await addSubOperationComment(assessment?.assessment_id, payload);

      const newComment = {
        asmnt_operation_comment_id: response?.data?.asmnt_operation_comment_id,
        asmnt_operation_id: response?.data?.asmnt_operation_id || "",
        comment: trimmedComment,
        created_at: new Date().toISOString(),
        updated_at: null,

      };
      setProofreadingComments((prev) => [newComment, ...prev]);
    } catch (error) {
      console.error("Error adding proofreading comment:", error);
      alert("Error adding comment: " + (error?.message || "Unknown error"));
    }



    setProofreadingComment("");


  }, [proofreadingComment]);

  const handleProofreadingCompletedChange = async () => {
    const employeeId = sessionStorage.getItem("manage_user_id") || "";
    const assessmentOperationId = operationStateLatest?.asmnt_operation_id || "";

    if (!assessment?.assessment_id || !employeeId || !assessmentOperationId || isWorkflowActionBusy) {
      return;
    }

    setWorkflowActionLoading("proofreading");

    try {
      const payload = {
        operation_id: operationStateLatest?.status_id,
      };

      const response = await finishProofreading(
        assessment?.assessment_id,
        employeeId,
        assessmentOperationId,
        payload
      );

      if (response?.success === false) {
        alert(response?.message || "Unable to finish proofreading review.");
        return;
      }

      setIsProofreadingCompleted(true);
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Error updating proofreading status:", error);
      alert("Error updating proofreading status: " + (error?.message || "Unknown error"));
    } finally {
      setWorkflowActionLoading("");
    }
  };

  const handleSetReviewForPaper = async () => {
    const assessmentId = assessment?.assessment_id || id;
    const asmntReviewId = currentReviewId;
    const reviewerId = sessionStorage.getItem("manage_user_id");

    if (!assessmentId || !asmntReviewId || !reviewerId || isWorkflowActionBusy) {
      return;
    }

    setWorkflowActionLoading("set-review");

    const payload = {
      assessment_state: 7
    };

    try {
      await set_review_for_paper(assessmentId, asmntReviewId, reviewerId, payload);
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Error setting review for paper:", error);
      alert("Error setting review for paper: " + (error?.message || "Unknown error"));
    } finally {
      setWorkflowActionLoading("");
    }
  };

  const handleSetBackToReviewByCompleted = async () => {
    const assessmentId = assessment?.assessment_id || id;
    const asmntReviewId = currentReviewId;
    const reviewerId = sessionStorage.getItem("manage_user_id");

    if (!assessmentId || !asmntReviewId || !reviewerId || isWorkflowActionBusy) {
      return;
    }

    setWorkflowActionLoading("set-review");

    try {
      const response = await set_for_review_by_completed(
        assessmentId,
        asmntReviewId,
        reviewerId,
        {}
      );

      if (response?.success === false) {
        alert(response?.message || "Unable to set paper back to review.");
        return;
      }

      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Error setting paper back to review:", error);
      alert("Error setting paper back to review: " + (error?.message || "Unknown error"));
    } finally {
      setWorkflowActionLoading("");
    }
  };

  const handelPaperReadyToPublish = async () => {
    const assessmentId = assessment?.assessment_id || id;
    const asmntReviewId = currentReviewId;
    const reviewerId = sessionStorage.getItem("manage_user_id");

    if (!assessmentId || !asmntReviewId || !reviewerId || isWorkflowActionBusy) {
      return;
    }

    setWorkflowActionLoading("ready-to-publish");

    const payload = {

    };

    try {
      await set_paper_to_publish(assessmentId, asmntReviewId, reviewerId, payload);
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Error setting paper as ready to publish:", error);
      alert("Error setting paper as ready to publish: " + (error?.message || "Unknown error"));
    } finally {
      setWorkflowActionLoading("");
    }
  };

  const handleReturnApproval = async () => {
    const assessmentId = assessment?.assessment_id || id;
    const asmntReviewId = currentReviewId;
    const reviewerId = sessionStorage.getItem("manage_user_id");

    if (!assessmentId || !asmntReviewId || !reviewerId || isWorkflowActionBusy) {
      return;
    }

    if (!isReadyToPublish) {
      alert(
        isReferBack
          ? "Reviewer comments need to be resolved"
          : "Review needs to be finish before finalizing"
      );
      return;
    }

    setWorkflowActionLoading("return-approval");

    const payload = {
      assessment_state: 6
    };

    try {
      await set_for_review_by_completed(assessmentId, asmntReviewId, reviewerId, payload);
      if (onBack) {
        onBack();
      }
    }
    catch (error) {
      console.error("Error returning approval:", error);
      alert("Error returning approval: " + (error?.message || "Unknown error"));
    } finally {
      setWorkflowActionLoading("");
    }
  };

  const handleFinalizePaper = async () => {
    const assessmentId = assessment?.assessment_id || id;
    const asmntReviewId = currentReviewId;
    const reviewerId = sessionStorage.getItem("manage_user_id");

    if (!assessmentId || !asmntReviewId || !reviewerId || isWorkflowActionBusy) {
      return;
    }

    if (!isReadyToPublish) {
      alert(
        isReferBack
          ? "Reviewer comments need to be resolved"
          : "Review needs to be finish before finalizing"
      );
      return;
    }

    setWorkflowActionLoading("finalize");

    const payload = {

    };

    try {
      await finalize_paper(assessmentId, asmntReviewId, reviewerId, payload);
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Error finalizing paper:", error);
      alert("Error finalizing paper: " + (error?.message || "Unknown error"));
    } finally {
      setWorkflowActionLoading("");
    }
  };

  const handlePublishPaperAfterProofreading = async () => {
    const assessmentId = assessment?.assessment_id || id;
    if (!assessmentId || isWorkflowActionBusy) {
      return;
    }

    setWorkflowActionLoading("publish");

    const payload = {
      status: assessmentStatusConstants.PUBLISHED.name,
    };

    try {
      const response = await updateAssessmentStatus(assessmentId, payload);
      if (response?.success === false) {
        alert(response?.message || "Unable to update assessment status.");
        return;
      }

      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Error publishing paper after proofreading:", error);
      alert("Error: " + (error?.message || "Unknown error"));
    } finally {
      setWorkflowActionLoading("");
    }
  };


  const renderQuestion = (question, index, sectionKey) => {

    const normalizedQuestion = normalizeQuestion(question, index);
    const subQuestions = Array.isArray(normalizedQuestion?.subquestions)
      ? dedupeSubQuestions(normalizedQuestion.subquestions)
      : [];
    const subQuestionTotalMarks = subQuestions.reduce(
      (sum, subQuestion) => sum + Number(subQuestion.mark_allocation || subQuestion.markAllocation || 0),
      0
    );
    const mcqQuestion = isMcqQuestion(normalizedQuestion);
    const { options, correctIndex } = getQuestionOptionStates(normalizedQuestion);


    const questionIdentity = getQuestionIdentity(normalizedQuestion);
    const commentsForQuestion = getCommentsForQuestion(questionIdentity);

    return (


      <article className="paper-question-review row g-3" key={`${sectionKey}-${normalizedQuestion.key}`}>

        <div className="col-12 col-lg-3">
          <div className="paper-review-info-row d-flex align-items-start mb-3">
            <div className="paper-review-checkbox me-3">
              {hideQuestionActions ? (
                isQuestionReviewed(questionIdentity) && (
                  <div className="reviewed-state-pill">
                    ✓ Marked as Reviewed
                  </div>
                )
              ) : (
                canMarkReviewed && (
                  <label className="form-check form-check-inline" style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      disabled={isQuestionReviewed(questionIdentity) || !canMarkReviewed}
                      checked={isQuestionReviewed(questionIdentity)}
                      onChange={() => handelChangeMrkedAsReviewed(questionIdentity)}
                    />
                    <span className="form-check-label">Mark as Reviewed</span>
                  </label>
                )
              )}
            </div>
            <div className="paper-review-info flex-grow-1">


              {commentsForQuestion.length > 0 && (
                <div className="paper-review-info__box paper-review-comments-list">
                  {commentsForQuestion.map((c) => (
                    <div className="paper-single-comment d-flex align-items-start mb-3" key={c.id}>
                      <div className="paper-review-avatar me-2">
                        {c.avatar ? (
                          <img src={c.avatar} alt={c.reviewer_name || "avatar"} />
                        ) : (
                          <div className="avatar-initials">{getInitials(c.reviewer_name)}</div>
                        )}
                      </div>
                      <div className="paper-review-meta">
                        <div className="paper-review-name">{c.reviewer_name || "Reviewer"}</div>
                        <div className="paper-review-handle">{c.reviewer_name ? `@ ${c.reviewer_name}` : ""}</div>
                        <p className="paper-review-comment mb-1">{c.comment}</p>
                        <div className="paper-review-time small text-muted">{formatTime(c.reviewer_created_at) || ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!hideQuestionActions && (
                <div className="paper-action-pills mt-2">
                  {canAddQuestionComment && (
                    <button
                      type="button"
                      className="pill-button pill-button--primary me-2"
                      aria-label="Add comment"
                      disabled={isQuestionReviewed(questionIdentity) || !canAddQuestionComment}
                      onClick={() => {
                        if (!isQuestionReviewed(questionIdentity)) {
                          openCommentDrawer("add", normalizedQuestion, normalizedQuestion.question_number ?? index + 1);
                        }
                      }}
                    >
                      + ADD COMMENT
                    </button>
                  )}

                  {(canEditQuestion || canEditProofreadingQuestion) && (
                    <button
                      type="button"
                      className="pill-button pill-button--secondary"
                      aria-label="Edit question"
                      onClick={() => editQuestion(normalizedQuestion, subQuestions)}
                    >
                      ✎ EDIT QUESTION
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-9">
          <div className="paper-question-review__card">
            <h2>
              {normalizedQuestion.question_number || index + 1}. {normalizedQuestion.title || `Question ${index + 1}`}
            </h2>
            <div className="paper-question-review__guidance">
              <strong>Question</strong>
              <p>{getRichTextContent(normalizedQuestion.content)}</p>
            </div>
            <div className="paper-question-review__guidance">
              <strong>{mcqQuestion ? "Answer Options" : "Model Answer"}</strong>
              {mcqQuestion ? (
                <div className="paper-mcq-options">
                  {options.length > 0 ? (
                    options.map((option, optionIndex) => {
                      const optionText = getDraftTextContent(option);
                      const isCorrect = correctIndex === optionIndex;

                      return (
                        <div
                          key={`${normalizedQuestion.key}-option-${optionIndex}`}
                          className={`paper-mcq-option ${isCorrect ? "paper-mcq-option--correct" : ""}`}
                        >
                          <span className="paper-mcq-option__bullet" aria-hidden="true">
                            {isCorrect ? "●" : "○"}
                          </span>
                          <span className="paper-mcq-option__text">{optionText}</span>
                          {isCorrect && <span className="paper-mcq-option__tag">Correct</span>}
                        </div>
                      );
                    })
                  ) : (
                    <p>No options available.</p>
                  )}
                </div>
              ) : (
                <p>{getRichTextContent(normalizedQuestion.answer)}</p>
              )}
            </div>
            {normalizedQuestion.guidance && (
              <div className="paper-question-review__guidance">
                <strong>Guidance</strong>
                <p>{getRichTextContent(normalizedQuestion.guidance)}</p>
              </div>
            )}
            {subQuestions.length > 0 && (
              <div className="paper-question-review__guidance">
                <strong>Sub Questions</strong>
                {subQuestions.map((subQuestion, subIndex) => (
                  <div key={`${normalizedQuestion.key}-sub-${subIndex}`} className="mt-2">
                    <p className="mb-1">
                      Sub Question {subIndex + 1}: {getRichTextContent(subQuestion.content)}
                    </p>
                    <p className="mb-0 text-muted">
                      Answer: {getRichTextContent(subQuestion.answer)}
                    </p>
                    <p className="mb-0 text-muted">
                      Allocated Marks: {getDisplayValue(subQuestion.mark_allocation || subQuestion.markAllocation, "0")}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <div className="paper-question-review__marks">
              Allocated Marks: {getDisplayValue(
                subQuestions.length > 0
                  ? subQuestionTotalMarks
                  : normalizedQuestion.mark_allocation || normalizedQuestion.markAllocation,
                "0"
              )}
            </div>
          </div>
        </div>


      </article>
    );
  };

  const renderPaperQuestionDisplay = (mainQuestion, subQuestions = [], questionNumber, showPassage = true) => {
    const { questionRaw, optionRaws } = getQuestionParts(mainQuestion);
    const isMcq = isMcqQuestion(mainQuestion);
    const isSubmission = isSubmissionQuestion(mainQuestion);
    const isSubQuestion = isSubQuestionType(mainQuestion) || subQuestions.length > 0;
    const correctIndexes = getMcqCorrectIndexes(mainQuestion);
    const attachmentFiles = parseAttachmentAnswer(mainQuestion.answer);

    const totalMarks =
      subQuestions.length > 0
        ? subQuestions.reduce(
          (sum, sub) => sum + Number(sub.mark_allocation || sub.markAllocation || 0),
          0
        )
        : Number(mainQuestion.mark_allocation || mainQuestion.markAllocation || 0);

    return (
      <div className="paper-review-render-card">
        <div className="paper-review-render-header">
          <div>
            <span className="paper-review-question-badge">Q{questionNumber}</span>
            {mainQuestion.is_optional && (
              <span className="badge badge-primary ml-2">Optional</span>
            )}
          </div>

          <strong>{totalMarks} Marks</strong>
        </div>

        {showPassage && mainQuestion.passage_content && (
          <div className="paper-review-render-block paper-review-render-block--passage">
            <strong>Passage</strong>
            {renderDraftContent(mainQuestion.passage_content)}
          </div>
        )}

        <div className="paper-review-render-block">
          <strong>Question</strong>
          {renderSingleDraft(questionRaw, "paper-review-draft-content")}
        </div>

        {isMcq && (
          <div className="paper-review-render-block">
            <strong>Answer Options</strong>

            <div className="row g-3 mt-1">
              {optionRaws.length > 0 ? (
                optionRaws.map((optionRaw, optionIndex) => {
                  const isCorrect = correctIndexes.includes(optionIndex);

                  return (
                    <div className="col-12 col-md-6" key={`${mainQuestion.key}-option-${optionIndex}`}>
                      <div
                        className={[
                          "exam-option",
                          "paper-review-mcq-option",
                          isCorrect ? "paper-review-mcq-option--correct" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        <span className="exam-option__indicator" />
                        <span className="exam-option__text">
                          {renderSingleDraft(optionRaw)}
                        </span>
                        {isCorrect && <span className="paper-mcq-option__tag">Correct</span>}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-muted mb-0">No options available.</p>
              )}
            </div>
          </div>
        )}

        {!isMcq && !isSubQuestion && (
          <div className="paper-review-render-block paper-review-render-block--answer">
            <strong>
              {isSubmission ? "Submission / Model Answer" : "Model Answer"}
            </strong>

            {isSubmission && attachmentFiles.length > 0 ? (
              <div className="d-flex flex-wrap mt-2" style={{ gap: 8 }}>
                {attachmentFiles.map((fileName) => (
                  <button
                    type="button"
                    className="exam-paper-chip"
                    key={fileName}
                    onClick={() => handleModelAnswerFileDownload(fileName)}
                  >
                    {fileName}
                  </button>
                ))}
              </div>
            ) : (
              renderDraftContent(
                mainQuestion.answer,
                "paper-review-draft-content"
              )
            )}
          </div>
        )}

        {mainQuestion.guidance && (
          <div className="paper-review-render-block paper-review-render-block--guidance">
            <strong>Marking Guidance</strong>
            {renderDraftContent(mainQuestion.guidance)}
          </div>
        )}

        {subQuestions.length > 0 && (
          <div className="paper-review-render-block">
            <strong>Sub Questions</strong>

            <div className="exam-subquestion-list mt-2">
              {subQuestions.map((subQuestion, subIndex) => {
                const normalizedSub = normalizeQuestion(subQuestion, subIndex);
                const { questionRaw: subQuestionRaw, optionRaws: subOptionRaws } =
                  getQuestionParts(normalizedSub);
                const subIsMcq = isMcqQuestion(normalizedSub);
                const subIsSubmission = isSubmissionQuestion(normalizedSub);
                const subCorrectIndexes = getMcqCorrectIndexes(normalizedSub);
                const subAttachmentFiles = parseAttachmentAnswer(normalizedSub.answer);

                return (
                  <div className="exam-subquestion-card" key={normalizedSub.key || normalizedSub.question_id || subIndex}>
                    <div className="exam-subquestion-title">
                      <span className="exam-subquestion-number">{subIndex + 1}.</span>
                      <div className="exam-subquestion-content">
                        {renderSingleDraft(subQuestionRaw)}
                      </div>
                    </div>

                    {subIsMcq ? (
                      <div className="row g-3 mt-2">
                        {subOptionRaws.map((optionRaw, optionIndex) => {
                          const isCorrect = subCorrectIndexes.includes(optionIndex);

                          return (
                            <div className="col-12 col-md-6" key={`${normalizedSub.key}-option-${optionIndex}`}>
                              <div
                                className={[
                                  "exam-option",
                                  "paper-review-mcq-option",
                                  isCorrect ? "paper-review-mcq-option--correct" : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                              >
                                <span className="exam-option__indicator" />
                                <span className="exam-option__text">
                                  {renderSingleDraft(optionRaw)}
                                </span>
                                {isCorrect && <span className="paper-mcq-option__tag">Correct</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="paper-review-sub-answer">
                        <strong>{subIsSubmission ? "Submission / Model Answer" : "Model Answer"}</strong>

                          {subIsSubmission && subAttachmentFiles.length > 0 ? (
                            <div className="d-flex flex-wrap mt-2" style={{ gap: 8 }}>
                              {subAttachmentFiles.map((fileName) => (
                                <button
                                  type="button"
                                  className="exam-paper-chip"
                                  key={fileName}
                                  onClick={() => handleModelAnswerFileDownload(fileName)}
                                >
                                  {fileName}
                                </button>
                              ))}
                            </div>
                          ) : (
                          renderDraftContent(normalizedSub.answer)
                        )}
                      </div>
                    )}

                    {normalizedSub.guidance && (
                      <div className="paper-review-sub-answer">
                        <strong>Guidance</strong>
                        {renderDraftContent(normalizedSub.guidance)}
                      </div>
                    )}

                    <div className="paper-question-review__marks mt-2">
                      Allocated Marks: {getDisplayValue(
                        normalizedSub.mark_allocation || normalizedSub.markAllocation,
                        "0"
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleModelAnswerFileDownload = async (fileName) => {
    try {
      const containerName = config.storageBlobConfigs.containerName;

      const cleanFileName = String(fileName)
        .replace(/['"]+/g, "")
        .trim();

      await AzureStorage.downloadBlob(containerName, cleanFileName);
    } catch (error) {
      console.error("File download failed", error);

      window.Swal.fire(
        "Failed!",
        "File download failed. Please try again.",
        "error"
      );
    }
  };

  const renderQuestionGroup = (group, index, sectionKey, showPassage = true) => {
    const mainQuestion = normalizeQuestion(group.mainQuestion, index);

    const subQuestions = resolveSubQuestions(mainQuestion, subQuestionMap);
    const subQuestionTotalMarks = subQuestions.reduce(
      (sum, subQuestion) => sum + Number(subQuestion.mark_allocation || subQuestion.markAllocation || 0),
      0
    );
    const mcqQuestion = isMcqQuestion(mainQuestion);
    const { options, correctIndex } = getQuestionOptionStates(mainQuestion);


    const questionIdentity = getQuestionIdentity(mainQuestion);
    const commentsForQuestion = getCommentsForQuestion(questionIdentity);

    return (
      <article className="paper-question-review row g-3" key={`${sectionKey}-${group.key}`}>


        <div className="col-12 col-lg-3">
          <div className="paper-review-info-row d-flex align-items-start mb-3">
            <div className="paper-review-checkbox me-3">
              {hideQuestionActions ? (
                isQuestionReviewed(questionIdentity) && (
                  <div className="reviewed-state-pill">
                    ✓ Marked as Reviewed
                  </div>
                )
              ) : (
                canMarkReviewed && (
                  <label className="form-check form-check-inline" style={{ cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      disabled={isQuestionReviewed(questionIdentity) || !canMarkReviewed}
                      checked={isQuestionReviewed(questionIdentity)}
                      onChange={() => handelChangeMrkedAsReviewed(questionIdentity)}
                    />
                    <span className="form-check-label">Mark as Reviewed</span>
                  </label>
                )
              )}
            </div>
            <div className="paper-review-info flex-grow-1">

              {commentsForQuestion.length > 0 && (
                <div className="paper-review-info__box paper-review-comments-list">
                  {commentsForQuestion.map((c) => (
                    <div className="paper-single-comment d-flex align-items-start mb-3" key={c.id}>
                      <div className="paper-review-avatar me-2">
                        {c.avatar ? (
                          <img src={c.avatar} alt={c.reviewer_name || "avatar"} />
                        ) : (
                          <div className="avatar-initials">{getInitials(c.reviewer_name)}</div>
                        )}
                      </div>
                      <div className="paper-review-meta">
                        <div className="paper-review-name">{c.reviewer_name || "Reviewer"}</div>
                        <div className="paper-review-handle">{c.reviewer_name ? `@ ${c.reviewer_name}` : ""}</div>
                        <p className="paper-review-comment mb-1">{c.comment}</p>
                        <div className="paper-review-time small text-muted">{formatTime(c.reviewer_created_at) || ""}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!hideQuestionActions && (
                <div className="paper-action-pills mt-2">
                  {canAddQuestionComment && (
                    <button
                      type="button"
                      className="pill-button pill-button--primary me-2"
                      aria-label="Add comment"
                      disabled={isQuestionReviewed(questionIdentity) || !canAddQuestionComment}
                      onClick={() => {
                        if (!isQuestionReviewed(questionIdentity)) {
                          openCommentDrawer("add", mainQuestion, group.questionNumber);
                        }
                      }}
                    >
                      + ADD COMMENT
                    </button>
                  )}

                  {(canEditQuestion || canEditProofreadingQuestion) && (
                    <button
                      type="button"
                      className="pill-button pill-button--secondary"
                      aria-label="Edit question"
                      onClick={() => editQuestion(mainQuestion, subQuestions)}
                    >
                      ✎ EDIT QUESTION
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-9">
          {renderPaperQuestionDisplay(mainQuestion, subQuestions, group.questionNumber, showPassage
          )}
        </div>

      </article>
    );
  };

  return (
    <ContentWrapper>
      <section className="paper-review-view">
        {selectedQuestionId ? (
          <PaperReviewQuestionEditPage
            assessmentId={assessment.assessment_id || id}
            questionId={selectedQuestion?.question_id || selectedQuestionId}
            question={selectedQuestion}
            onSave={handleQuestionUpdated}
            onBack={() => {
              setSelectedQuestionId(null);
              setSelectedQuestionData(null);
            }}
          />
        ) : (
          <>
            <div className="paper-review-view__top row g-2 align-items-center">
              <div className="col-12 col-md">
                <BackButton
                  label=""
                  onClick={() => {
                    if (onBack) {
                      onBack();
                    } else {
                      history.push(routes.ppr_rew);
                    }
                  }}
                  title="Back"
                />
              </div>


              <div className="col-12 col-md-auto paper-review-view__top-action paper-review-view__top-action--finish paper-review-view__finish-actions">
                {canFinishOperation && (
                  <MainButton
                    action="save"
                    icon="save"
                    label={`Finish Proofreading Review`}
                    disabled={isProofredingComplteted || isWorkflowActionBusy}
                    loading={workflowActionLoading === "proofreading"}
                    className="paper-review-view__finish-button paper-review-view__finish-button--proofreading"
                    onClick={() => handleProofreadingCompletedChange()}
                    />
                  )}
                  {EmployeeType === "TEACH" && isAssignedReviewer && ["review", "inreview"].includes(assessmentState) && (
                    <MainButton
                      action="save"
                      icon="save"
                      label={`Finish Review`}
                      disabled={isReviewFinished || isWorkflowActionBusy || !allQuestionsReviewed}
                      className="paper-review-view__finish-button paper-review-view__finish-button--review"
                      onClick={() => {
                        if (isReviewFinished) {
                        return;
                      }

                      if (!allQuestionsReviewed) {
                        setFinishReviewError("Please mark all questions as reviewed before finishing the review.");
                        return;
                      }

                      setFinishReviewError("");
                      setOpenFinishReviewModal(true);
                    }}
                  />
                )}
                {canSetForReview && (
                  <MainButton
                    action="save"
                    icon="save"
                    label={`Set For Review`}
                    disabled={isWorkflowActionBusy}
                    loading={workflowActionLoading === "set-review"}
                    className="paper-review-view__finish-button paper-review-view__finish-button--set-review"
                      onClick={() => handleSetReviewForPaper()}
                    />
                  )}
                  {canReturnApproval && (
                    <div title={returnApprovalDisabledMessage}>
                      <MainButton
                        action="save"
                        icon="save"
                        label="Return Approval"
                        disabled={!isReadyToPublish || isWorkflowActionBusy}
                        loading={workflowActionLoading === "return-approval"}
                        className="paper-review-view__finish-button paper-review-view__finish-button--return-approval"
                        onClick={handleReturnApproval}
                      />
                    </div>
                  )}
                  {canFinalizePaper && (
                    <div title={finalizeDisabledMessage}>
                      <MainButton
                        action="save"
                        icon="save"
                        label="Finalize Paper & Publish"
                        disabled={!isReadyToPublish || isWorkflowActionBusy}
                        loading={workflowActionLoading === "finalize"}
                        className="paper-review-view__finish-button paper-review-view__finish-button--finalize"
                        onClick={handleFinalizePaper}
                      />
                    </div>
                  )}
                  {/* {(workflowStatus === "proofreading" && item.operation_state && item.operation_state.length > 0 && EmployeeType == "MASAD") && (
                  <MainButton
                    action="save"
                    icon="save"
                    label={` Publish Paper`}
                    disabled={isWorkflowActionBusy}
                    loading={workflowActionLoading === "finalize"}
                    className="paper-review-view__finish-button paper-review-view__finish-button--finalize"
                    onClick={() => handlePublishPaperAfterProofreading()}
                  />
                )} */}

                {canSetBackToReview && (
                  <MainButton
                    action="save"
                    icon="save"
                    label={`Set Back To Review`}
                    disabled={isWorkflowActionBusy}
                    loading={workflowActionLoading === "set-review"}
                    className="paper-review-view__finish-button paper-review-view__finish-button--return-approval"
                    onClick={() => handleSetBackToReviewByCompleted()}
                  />
                )}

                {canSetReadyToPublish && (
                  <MainButton
                    action="save"
                    icon="save"
                    label={`Set Ready To Publish`}
                    disabled={isWorkflowActionBusy}
                    loading={workflowActionLoading === "ready-to-publish"}
                    className="paper-review-view__finish-button paper-review-view__finish-button--return-approval"
                    onClick={() => handelPaperReadyToPublish()}
                  />
                )}
              </div>
              <div className="col-12 col-md-auto text-md-end">
                <MainButton
                  action="comments"
                  icon="comments"
                  label={`View Comments ${aggregatedComments.length > 0 ? `(${aggregatedComments.length})` : ""}`}
                  onClick={() => openCommentDrawer("view")}
                />
              </div>
            </div>




              {/* <div className="paper-review-preview-header row g-2 align-items-center">
              <div className="col-12 col-md-2">
                <div className="student-paper-brand">
                  <span className="student-paper-logo" />
                  <strong>Edfoci</strong>
                </div>
              </div>
              <div className="col-12 col-md-10">
                <div className="student-paper-heading">
                  <h1>{assessment.title || "Assessment Review"}</h1>
                  <div className="student-paper-line" />
                  <div className="student-paper-meta">
                    <span>{assessment.is_multiple ? "Multiple papers" : "Single paper"}</span>
                    <span>{assessment.assessment_state || "N/A"}</span>
                    <span>Parts: {getDisplayValue(assessment.no_of_parts, "0")}</span>
                    <span>Attempts: {getDisplayValue(assessment.attempt_no_id)}</span>
                    <span
                      style={{
                        color: "#fff",
                        backgroundColor: "#d32f2f",
                        padding: "4px 8px",
                        borderRadius: "6px",
                        fontWeight: "bold",
                      }}
                    >
                      Total Marks: {getDisplayValue(totalAllocatedMarks, "0")}
                    </span>
                  </div>
                </div>
              </div>
              </div> */}

              {paperHeaderAssessment && (
                <div className="paper-review-preview-header mb-4">
                  <StudentPaperHeader
                    assessment={paperHeaderAssessment}
                    showStartTime={false}
                  />
                </div>
              )}

              <div className="paper-question-review__card mb-4">
                <div className="row g-2">
                  {assessmentDetailItems.map((item) => (
                    <div className="col-auto" key={item.label}>
                      <div className="paper-question-review__marks">
                        {item.label}: {item.value}
                      </div>
                    </div>
                  ))}
                </div>
                {assessment.additional_info && (
                  <div className="paper-question-review__guidance mt-3">
                    <strong>Additional Info</strong>
                    <p>{assessment.additional_info}</p>
                  </div>
                )}
              </div>

              {!isReviewFinished && finishReviewError && (
                <div className="alert alert-warning" role="alert">
                  {finishReviewError}
              </div>
            )}

            {isLoading && (
              <div className="alert alert-info" role="status">
                Loading assessment details...
              </div>
            )}

            {loadError && (
              <div className="alert alert-danger" role="alert">
                {loadError}
              </div>
            )}

            {EmployeeType !== "TEACH" && (
              <div className="paper-question-review__card mb-4">
                <h3 className="mb-3">ProofReading Comments for Paper</h3>

                {proofreadingComments.length > 0 && (
                  <div className="paper-review-info__box paper-review-comments-list mb-4">
                    {proofreadingComments.map((comment) => (
                      <div className="paper-single-comment d-flex align-items-start mb-3" key={comment.id}>
                        <div className="paper-review-avatar me-2">
                          <div className="avatar-initials">{getInitials(proofreadingAuthorName)}</div>
                        </div>
                        <div className="paper-review-meta">
                          <div className="paper-review-name">{proofreadingAuthorName}</div>
                          {comment.comment && <p className="paper-review-comment mb-1">{comment.comment}</p>}
                          <div className="paper-review-time small text-muted">{formatTime(comment.created_at || comment.timestamp)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {proofreadingComments.length === 0 && (
                  <div className="text-muted mb-4">
                    No proofreading comments for paper yet
                  </div>
                )}

                {canAddProofreadingComment && (
                  <div className="proofreading-comment-input">
                    <div className="mb-3">
                      <label htmlFor="proofreadingCommentText" className="form-label">
                        <strong>Add Proofreading Comment</strong>
                      </label>
                      <textarea
                        id="proofreadingCommentText"
                        className="form-control"
                        rows="4"
                        placeholder="Enter your proofreading comment here..."
                        value={proofreadingComment}
                        onChange={(e) => setProofreadingComment(e.target.value)}
                      />
                    </div>

                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={!canAddProofreadingComment || isWorkflowActionBusy}
                        onClick={handleProofreadingCommentSubmit}
                      >
                        Submit Proofreading Comment
                      </button>
                    </div>

                  </div>
                )}
              </div>

            )}

            <div className="paper-question-review-list">
              {groupedQuestionBlocks.length > 0 ? (
                groupedQuestionBlocks.map((partGroup, partIndex) => {
                  const partTotalMarks = partGroup.sections.reduce((partTotal, sectionGroup) => {
                    const questionGroups = sectionGroup.questions.map((question, index) => {
                      const subQuestions = resolveSubQuestions(question, subQuestionMap);

                      return {
                        key: String(getQuestionId(question) || index),
                        questionNumber: question.question_number ?? index + 1,
                        mainQuestion: question,
                        subQuestions,
                      };
                    });

                    return partTotal + calculateTotalAllocatedMarks(questionGroups);
                  }, 0);

                  return (
                    <div className="paper-question-review__part mb-5" key={partGroup.partKey}>
                      {(partGroup.partName || partGroup.partInstruction) && (
                        <div className="paper-question-review__card paper-question-review__part-card mb-3">
                          <h2 className="mb-2">
                            {partGroup.partName || `Part ${partIndex + 1}`}
                          </h2>
                          <div className="student-paper-meta">
                            <span>Sections: {getDisplayValue(partGroup.sections.length, "0")}</span>
                            <span>Allocated Marks: {getDisplayValue(partTotalMarks, "0")}</span>
                          </div>

                          {partGroup.partInstruction && (
                            <div className="paper-question-review__guidance paper-question-review__part-instruction">
                              <strong>Part Instruction</strong>
                              <p>{getRichTextContent(partGroup.partInstruction)}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {!partGroup.partName && !partGroup.partInstruction && (
                        <div className="paper-question-review__card paper-question-review__part-card mb-3">
                          <h2 className="mb-2">Part {partIndex + 1}</h2>
                          <div className="student-paper-meta">
                            <span>Questions: {getDisplayValue(mainQuestionList.length, "0")}</span>
                            <span>Allocated Marks: {getDisplayValue(totalAllocatedMarks, "0")}</span>
                          </div>
                        </div>
                      )}

                      {partGroup.sections.map((sectionGroup, sectionIndex) => {
                        const questionGroups = sectionGroup.questions.map((question, index) => {
                          const subQuestions = resolveSubQuestions(question, subQuestionMap);

                          return {
                            key: String(getQuestionId(question) || index),
                            questionNumber: question.question_number ?? index + 1,
                            mainQuestion: question,
                            subQuestions,
                          };
                        });

                        const groupedByPassage = groupQuestionsByPassage(questionGroups);

                        return (
                          <div
                            className="paper-question-review__card paper-question-review__section-card mb-4"
                            key={sectionGroup.sectionKey}
                          >
                            <h3 className="mb-2">
                              {sectionGroup.sectionName || `Section ${sectionIndex + 1}`}
                            </h3>

                            <div className="student-paper-meta mb-3">
                              <span>Questions: {getDisplayValue(questionGroups.length, "0")}</span>
                              <span>Allocated Marks: {getDisplayValue(calculateTotalAllocatedMarks(questionGroups), "0")}</span>
                            </div>

                            {sectionGroup.sectionInstruction && (
                              <div className="paper-question-review__guidance mb-3">
                                <strong>Section Instruction</strong>
                                <p>{sectionGroup.sectionInstruction}</p>
                              </div>
                            )}

                            {questionGroups.length > 0 ? (
                              <div className="paper-question-review-list">
                                {groupedByPassage.map((passageGroup, groupIndex) => (
                                  <div
                                    key={
                                      passageGroup.passageId
                                        ? `passage-${passageGroup.passageId}`
                                        : `question-${groupIndex}`
                                    }
                                    className="mb-4"
                                  >
                                    {passageGroup.passageId && (
                                      <div className="paper-review-render-card mb-3">
                                        <div className="paper-review-render-block paper-review-render-block--passage">
                                          {renderDraftContent(passageGroup.passageContent)}
                                        </div>
                                      </div>
                                    )}

                                    {passageGroup.questions.map((group, questionIndex) =>
                                      renderQuestionGroup(
                                        group,
                                        questionIndex,
                                        sectionGroup.sectionKey,
                                        !passageGroup.passageId
                                      )
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="paper-review-empty">No questions in this section.</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })
                ) : (
                  <div className="paper-review-empty">No questions found.</div>
                )}
              </div>

              <FinishReviewModal
                open={openFinishReviewModal}
                onClose={() => {
                  setOpenFinishReviewModal(false);
                  setFinishReviewError("");
                  onBack();
                }}
                onFinish={handleFinishReview}
                options={finishReviewOptions}

              />

            <PaperCommentsDrawer
              open={commentsOpen}
              mode={commentsDrawerMode}
              question={commentTargetQuestion}
              draftComment={commentDraft}
              commentError={commentError}
              commentSubmitting={commentSubmitting}
              allComments={aggregatedComments}
              comments={(assessmentDetails?.comments || []).filter((comment) =>
                getCommentText(comment?.comment, comment?.text, comment?.content)
              )}
              onClose={closeCommentDrawer}
              onDraftCommentChange={setCommentDraft}
              onSubmitComment={handleSubmitComment}
            />
          </>
        )}
      </section>
    </ContentWrapper>
  );
}
